<template>
    <div class="p-grid">
        <div class="p-col-12">
            <div class="card">
                <h4 v-if="client && client.key && site && site.key" class="p-d-flex p-ai-baseline">
                    <router-link icon="pi pi-angle-left" class="p-button p-button-icon p-mr-4" :to="{ name: 'asset', params: { client_id: client.key, site_id: site.key, id: asset.key } }">
                        <i class="pi pi-angle-left"></i>
                        Back
                    </router-link>
                    <span>
                        <router-link :to="{ name: 'sites', params: { id: client.key } }">{{ client.name }}</router-link> /
                        <router-link :to="{ name: 'site', params: { client_id: client.key, id: site.key } }">
                            {{ site.name }}
                        </router-link> /
                        <router-link :to="{ name: 'asset', params: { client_id: client.key, site_id: site.key, id: asset.key } }">Asset
                            #{{ asset.asset_id }}
                        </router-link> /
                        Fault Log
                    </span>
                </h4>
                <DataTable class="fault_table" ref="dt" :value="faults" dataKey="unix" sortField="unix" :sortOrder="-1" :loading="faultsLoading" :paginator="true" :rows="6">
                    <template #empty>
                        No Faults found.
                    </template>

                    <template #loading>
                        Loading faults. Please wait.
                    </template>

                    <template #header>
                        <div class="p-d-flex p-jc-between p-ai-center" style="gap: 1rem;">
                            <p class="p-mb-0">Number of Fault Events: <strong>{{ faults ? faults.length : 0 }}</strong></p>
                            <Button icon="pi pi-external-link" label="Export" @click="exportCSV($event)" />
                        </div>
                    </template>

                    <Column field="fault_id" header="ID" :sortable="true" filter filterPlaceholder="Search by ID">
                        <template #body="slotProps">
                            {{ slotProps.data.fault_id }}
                        </template>
                    </Column>

                    <Column field="fault_reporter_name" header="Raised by Name" :sortable="true">
                        <template #body="slotProps">
                            {{ slotProps.data.fault_reporter_name }}
                        </template>
                    </Column>

                    <Column field="fault_reporter_company" header="Raised by Company" :sortable="true">
                        <template #body="slotProps">
                            {{ slotProps.data.fault_reporter_company }}
                        </template>
                    </Column>

                    <Column field="fault_report_note" header="Notes" :sortable="true">
                        <template #body="slotProps">
                            <p class="fault_note" :title="slotProps.data.fault_report_note">
                                {{ truncatedNote(slotProps.data.fault_report_note) }}
                            </p>
                        </template>
                    </Column>

                    <Column field="unix" header="Timestamp" :sortable="true">
                        <template #body="slotProps">
                            {{ slotProps.data.timestamp }}
                        </template>
                    </Column>

                    <Column field="fault_status" header="Status" :sortable="true">
                        <template #body="slotProps">
                            <div class="p-d-flex" style="align-items: baseline;">
                                <i class="status_icon" :style="{ 'background-color': slotProps.data.meta.hex }"></i>
                                <span>
                                    {{ slotProps.data.meta.label }}
                                </span>
                            </div>
                        </template>
                    </Column>
                </DataTable>
            </div>
        </div>
    </div>
</template>

<script>
import { db } from '@/firebase'
import { FilterMatchMode } from 'primevue/api'
import moment from 'moment'
import axios from 'axios'

export default {
    data() {
        return {
            faults: [],
            filterFaults: {
                global: { value: null, matchMode: FilterMatchMode.CONTAINS },
                fault_id: { value: null, matchMode: FilterMatchMode.CONTAINS },
            },
            faultsLoading: true,
            fault_statuses: [
                { label: 'Fault', value: 'active', hex: '#f31b2b' },
                { label: 'In Progress', value: 'in_progress', hex: '#ffbf00' },
                { label: 'Rectified', value: 'rectified', hex: '#1f5ea4' }
            ],
        }
    },
    firestore() {
        return {
            site: db.collection('sites').doc(this.$route.params.id),
            client: db.collection('clients').doc(this.$route.params.client_id),
            asset: db.collection('assets').doc(this.$route.params.asset_id),
        }
    },
    mounted() {
        this.getAllFaults();
    },
    methods: {
        exportCSV() {
            // this.$refs.dt.exportCSV();
            let faults = this.faults
            faults.sort((a, b) => b.unix - a.unix); // sort by timestamp

            // Get table data
            const dataToExport = faults.map((fault) => {
                return {
                    'ID': this.sanitizeForCSV(fault.fault_id),
                    'Raised by Name': this.sanitizeForCSV(fault.fault_reporter_name),
                    'Raised by Company': this.sanitizeForCSV(fault.fault_reporter_company),
                    'Notes': this.sanitizeForCSV(fault.fault_report_note),
                    'Timestamp': this.sanitizeForCSV(fault.timestamp),
                    'Status': this.sanitizeForCSV(fault.meta.label)
                };
            });

            // Convert the data into CSV format
            const csvData = this.generateCSV(dataToExport);

            let clientName = this.client.name;
            let siteName = this.site.name;
            let currentDate = moment(new Date()).format('YYYY-MM-DD');

            let fileName = clientName + '-' + siteName + '-' + currentDate;

            // Trigger the download
            const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', fileName + '.csv');
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        // Helper method to sanitize each field for CSV export
        sanitizeForCSV(value) {
            if (value === null || value === undefined) {
                return ''; // Handle null/undefined values as empty strings
            }
            // Convert the value to a string
            let str = String(value);

            // If the field contains commas, quotes, or newlines, wrap it in double quotes
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                // Escape double quotes by replacing " with ""
                str = str.replace(/"/g, '""');
                // Wrap the field in double quotes
                return `"${str}"`;
            }
            return str;
        },
        // Helper function to convert JSON data to CSV format
        generateCSV(data) {
            const header = Object.keys(data[0]).join(',') + '\n';
            const rows = data.map((row) => Object.values(row).join(',')).join('\n');
            return header + rows;
        },
        getAllFaults() {
            this.faultsLoading = true
            const getFaultLogEndpoint = process.env.VUE_APP_API_URL + '/get-asset-fault-log'

            axios({
                method: 'post',
                url: getFaultLogEndpoint,
                data: {
                    // all these are required to hit this function, so shouldn't be a problem
                    'asset_id': this.$route.params.asset_id
                }
            }).then((response) => {
                this.faults = response.data
                this.faults.forEach(fault => {
                    fault.meta = this.setIndividualFaultStatus(fault.fault_status)
                    fault.unix = moment(fault.timestamp, "DD/MM/YYYY @ hh:mma").unix() // unix required for sorting
                });
                this.faultsLoading = false
            }).catch(error => { console.error(error) });
        },
        truncatedNote(note) {
            return note.length > 200
                ? note.substring(0, 200) + "..."
                : note;
        },
        setIndividualFaultStatus(faultValue) {
            // console.log(this.current_fault_status)
            return this.fault_statuses.find(x => x.value == faultValue)
        },
        format_date(value) {
            if (value) {
                return moment.unix(value).format('DD/MM/YYYY @ hh:mma')
            }
        }
    },
}
</script>

<style lang="scss">
@media print {
    #print button {
        display: none;
    }
}

.status_icon {
    margin-right: 0.5rem;
}

.status_icon+span {
    white-space: nowrap;
}
</style>
